import React, { useState, useEffect, useRef } from "react";
import Chart from 'chart.js/auto';
import API_BASE_URL from 'config/api';
import {
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Snackbar,
  Alert,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import { BlueButton } from "styles";
import { useLocation } from "react-router-dom";
import { WhiteButton } from "styles";

const USER_DATA_KEY = "userMeData";

const CookieCategories = () => {
  // Chart ref for Chart.js
  const chartRef = useRef(null);
  // Cache ref for fetched summary data
  const cacheRef = useRef({});
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cookieData, setCookieData] = useState({});
  const [selectedSite, setSelectedSite] = useState("");
  // Removed: selectedLanguage, selectedCategory, openAddDialog, newCookie, editMode, editingCookie
  const [successMessage, setSuccessMessage] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("Bu Ay");
  const location = useLocation();

  // Styling constants
  const formControlStyles = {
    mb: 2,
    "& .MuiOutlinedInput-root": {
      borderRadius: "0.375rem",
      background: "#fafbfc",
      fontSize: 16,
      padding: "4px 12px",
      "& .MuiSelect-icon": {
        color: "#000",
        right: "12px",
      },
    },
    "& .MuiInputLabel-root": {
      fontWeight: 500,
      color: "#455a64",
      fontSize: 15,
    },
  };

  const selectStyles = {
    minHeight: 48,
    fontSize: 16,
    borderRadius: "12px",
    ".MuiSelect-select": {
      padding: "14px 32px 14px 12px",
    },
    "& .MuiSelect-icon": {
      display: "block !important",
    },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        borderRadius: "12px",
        marginTop: "8px",
        boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.1)",
      },
    },
  };

  // Helper functions
  const getCachedUserData = () => {
    const cachedData = sessionStorage.getItem(USER_DATA_KEY);
    return cachedData ? JSON.parse(cachedData) : null;
  };


  const loadUserData = async () => {
    try {
      const cachedData = getCachedUserData();
      if (cachedData) return cachedData;

      const response = await fetch(`${API_BASE_URL}/me`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
      });
      const data = await response.json();
      if (response.ok && data) {
        sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(data));
        return data;
      }
      throw new Error("Kullanıcı verisi alınamadı");
    } catch (err) {
      setError(err.message);
      return null;
    }
  };


  // Initial data loading
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Only user data from session or API, no cookie data logic
        const cachedUserData = getCachedUserData();

        if (cachedUserData) {
          setUserData(cachedUserData);

          const client = cachedUserData.user.clients?.[0];
          if (client?.sites?.length > 0) {
            const searchParams = new URLSearchParams(location.search);
            const siteId = searchParams.get("siteId") || client.sites[0].siteId;
            setSelectedSite(siteId);
            // Set default period
            setSelectedPeriod("Bu Hafta");
            // Initialize startDate and endDate for "Bu Hafta"
            const today = new Date();
            const day = today.getDay();
            const diffToMonday = day === 0 ? -6 : 1 - day;
            const start = new Date(today);
            start.setDate(today.getDate() + diffToMonday);
            const end = new Date(start);
            end.setDate(start.getDate() + 6);
            setStartDate(start.toISOString().split("T")[0]);
            setEndDate(end.toISOString().split("T")[0]);
          }
          return;
        }

        // If no cached data, fetch from API
        const user = await loadUserData();
        if (!user) return;

        setUserData(user);
        const client = user.user.clients?.[0];
        if (!client) return;

        const searchParams = new URLSearchParams(location.search);
        const siteId = searchParams.get("siteId") || client.sites[0].siteId;
        setSelectedSite(siteId);
        // Set default period
        setSelectedPeriod("Bu Hafta");
        // Initialize startDate and endDate for "Bu Hafta"
        const today = new Date();
        const day = today.getDay();
        const diffToMonday = day === 0 ? -6 : 1 - day;
        const start = new Date(today);
        start.setDate(today.getDate() + diffToMonday);
        const end = new Date(start);
        end.setDate(start.getDate() + 6);
        setStartDate(start.toISOString().split("T")[0]);
        setEndDate(end.toISOString().split("T")[0]);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [location.search]);

  // Chart rendering effect
  // Helper to render chart from summary (shared by cache and API)
  const renderChartFromSummary = (summary) => {
    const categories = Object.keys(summary);
    const categoryDisplayNames = {
      MarketingCookies: "Pazarlama",
      FunctionalCookies: "İşlevsel",
      PerformanceCookies: "Performans"
    };

    // Destroy existing chart if any
    if (chartRef.current.chartInstance) {
      chartRef.current.chartInstance.destroy();
      chartRef.current.chartInstance = null;
    }
    // Remove all children (canvases) before appending new one
    while (chartRef.current.firstChild) {
      chartRef.current.removeChild(chartRef.current.firstChild);
    }

    // Before appending: show fallback if no data
    if (categories.length === 0) {
      const noDataMessage = document.createElement("p");
      noDataMessage.textContent = "Seçilen tarih aralığında veri bulunamadı.";
      noDataMessage.style.padding = "16px";
      noDataMessage.style.textAlign = "center";
      chartRef.current.appendChild(noDataMessage);
      return;
    }

    // Single grouped bar chart comparing accepted/rejected per category
    const canvas = document.createElement("canvas");
    canvas.style.maxWidth = "100%";
    canvas.style.height = "480px";
    canvas.style.objectFit = "contain";
    canvas.style.width = "100%";

    const chartLabels = ["Kabul Edilen", "Reddedilen"];
    const datasetColors = ["#1976d2", "#4caf50", "#ff9800"];
    // Datasets: one per category, each with [accepted, rejected]
    const datasets = categories.map((cat, i) => ({
      label: categoryDisplayNames[cat] || cat,
      data: [
        summary[cat]?.accepted || 0,
        summary[cat]?.rejected || 0
      ],
      backgroundColor: datasetColors[i % datasetColors.length],
      barThickness: 40,
    }));

    chartRef.current.chartInstance = new Chart(canvas, {
      type: "bar",
      data: {
        labels: chartLabels,
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "top",
            labels: {
              font: { size: 14 }
            }
          },
          title: {
            display: true,
            text: "Kategoriye Göre Ziyaretçi Tercihleri",
            font: { size: 20 }
          }
        },
        scales: {
          x: {
            stacked: false,
            ticks: {
              font: { size: 15 }
            }
          },
          y: {
            beginAtZero: true,
            ticks: {
              font: { size: 14 }
            }
          }
        }
      }
    });

    chartRef.current.appendChild(canvas);
  };

  useEffect(() => {
    console.log("Chart trigger check", { chartRef: chartRef.current, selectedSite, startDate, endDate });
    if (!chartRef.current || !selectedSite || !startDate || !endDate) return;
    // Prevent redundant fetches if parameters haven't changed
    if (
      chartRef.current.lastStartDate === startDate &&
      chartRef.current.lastEndDate === endDate &&
      chartRef.current.lastSiteId === selectedSite
    ) {
      return; // Aynı sorgu tekrar yapılmasın
    }
    chartRef.current.lastStartDate = startDate;
    chartRef.current.lastEndDate = endDate;
    chartRef.current.lastSiteId = selectedSite;

    const fetchDataAndRenderCharts = async () => {
      try {
        const cacheKey = `${selectedSite}_${startDate}_${endDate}`;
        if (cacheRef.current[cacheKey]) {
          console.log("📦 Using cached data for", cacheKey);
          renderChartFromSummary(cacheRef.current[cacheKey]);
          return;
        }
        const token = sessionStorage.getItem("authToken");
        const queryParams = new URLSearchParams({
          siteId: selectedSite,
          startDate,
          endDate
        }).toString();

        console.log("🔁 Fetching data from API...");
        const response = await fetch(`${API_BASE_URL}/consents/summary?${queryParams}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) throw new Error("Özet verisi alınamadı");

        const summary = await response.json();
        cacheRef.current[cacheKey] = summary;
        renderChartFromSummary(summary);
      } catch (err) {
        console.error("Grafik verisi hatası:", err);
      }
    };

    fetchDataAndRenderCharts();
  }, [selectedSite, startDate, endDate]);

  // Data getters


  // Removed: handleAddCookie and all cookie definition editing logic

  // Render loading state
  if (loading && !userData) {
    return (
      <DashboardLayout>
        <MDBox my={3} display="flex" justifyContent="center">
          <CircularProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  // Render error state
  if (error) {
    return (
      <DashboardLayout>
        <MDBox my={3} display="flex" justifyContent="center">
          <Typography color="error">{error}</Typography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const client = userData?.user.clients?.[0];

  // Main render
  return (
    <DashboardLayout>
      <MDBox mt={2} mb={4}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h4" gutterBottom>
                  İstatistik
                </Typography>

                {/* Domain Selection Only */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth sx={formControlStyles}>
                      <InputLabel>Domain</InputLabel>
                      <Select
                        value={selectedSite || ""}
                        onChange={(e) => setSelectedSite(e.target.value)}
                        label="Domain"
                        sx={selectStyles}
                        MenuProps={menuProps}
                        IconComponent={(props) => (
                          <KeyboardArrowDownIcon {...props} fontSize="medium" />
                        )}
                      >
                        {client?.sites?.map((site) => (
                          <MenuItem key={site.siteId} value={site.siteId}>
                            {site.domain}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6} display="flex" justifyContent="flex-end" alignItems="center">
                    <Box width="100%" display="flex" justifyContent="flex-end">
                      {/* <BlueButton onClick={handleDownload} disabled={!selectedSite || !startDate || !endDate || loading}>
                        Verileri İndir
                      </BlueButton> */}
                    </Box>
                  </Grid>
                </Grid>

                <Grid container spacing={3}>
                  {/* Left panel: vertical filters */}
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Filtreler</Typography>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle1" gutterBottom>Dönem</Typography>
                        {["Bu Hafta", "Geçen Hafta", "Bu Ay", "Geçen Ay", "Özel Tarih"].map((label) => (
                          <Button
                            key={label}
                            fullWidth
                            variant={selectedPeriod === label ? "contained" : "outlined"}
                            sx={{
                              mb: 1,
                              color: selectedPeriod === label ? "#fff" : "#000"
                            }}
                            onClick={() => {
                              setSelectedPeriod(label);
                              const today = new Date();
                              let start, end;

                              switch (label) {
                                case "Bu Hafta": {
                                  // Pazartesi başlangıç, Pazar bitiş
                                  const day = today.getDay();
                                  const diffToMonday = day === 0 ? -6 : 1 - day;
                                  start = new Date(today);
                                  start.setDate(today.getDate() + diffToMonday);
                                  end = new Date(start);
                                  end.setDate(start.getDate() + 6);
                                  break;
                                }
                                case "Geçen Hafta": {
                                  // Önceki haftanın Pazartesi'sinden Pazar'ına
                                  const day = today.getDay();
                                  const diffToLastMonday = day === 0 ? -13 : -6 - day;
                                  start = new Date(today);
                                  start.setDate(today.getDate() + diffToLastMonday);
                                  end = new Date(start);
                                  end.setDate(start.getDate() + 6);
                                  break;
                                }
                                case "Bu Ay": {
                                  // Ayın ilk ve son günü
                                  start = new Date(today.getFullYear(), today.getMonth(), 1);
                                  end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                                  break;
                                }
                                case "Geçen Ay": {
                                  // Önceki ayın ilk ve son günü
                                  start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                                  end = new Date(today.getFullYear(), today.getMonth(), 0);
                                  break;
                                }
                                case "Özel Tarih": {
                                  // Add date limit logic
                                  const todayISO = new Date().toISOString().split("T")[0];
                                  const maxStart = new Date();
                                  maxStart.setDate(maxStart.getDate() - 60);
                                  const minDateISO = maxStart.toISOString().split("T")[0];
                                  setStartDate(minDateISO);
                                  setEndDate(todayISO);
                                  return; // Elle giriş yapılacak, tarih atanmaz
                                }
                              }

                              setStartDate(start?.toISOString().split("T")[0]);
                              setEndDate(end?.toISOString().split("T")[0]);
                            }}
                          >
                            {label}
                          </Button>
                        ))}
                        {selectedPeriod === "Özel Tarih" && (
                          <>
                            <FormControl fullWidth sx={{ mb: 2 }}>
                              <TextField
                                label="Başlangıç Tarihi"
                                type="date"
                                value={startDate}
                                onChange={(e) => {
                                  const newStart = e.target.value;
                                  const newEnd = endDate;
                                  const diffDays =
                                    new Date(newEnd) - new Date(newStart) > 0
                                      ? (new Date(newEnd) - new Date(newStart)) / (1000 * 60 * 60 * 24)
                                      : 0;
                                  if (diffDays > 60) {
                                    setError("En fazla 60 günlük bir aralık seçebilirsiniz.");
                                  } else {
                                    setStartDate(newStart);
                                  }
                                }}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                inputProps={{
                                  max: new Date().toISOString().split("T")[0],
                                  min: (() => {
                                    const d = new Date();
                                    d.setDate(d.getDate() - 60);
                                    return d.toISOString().split("T")[0];
                                  })()
                                }}
                              />
                            </FormControl>
                            <FormControl fullWidth sx={{ mb: 2 }}>
                              <TextField
                                label="Bitiş Tarihi"
                                type="date"
                                value={endDate}
                                onChange={(e) => {
                                  const newEnd = e.target.value;
                                  const newStart = startDate;
                                  const diffDays =
                                    new Date(newEnd) - new Date(newStart) > 0
                                      ? (new Date(newEnd) - new Date(newStart)) / (1000 * 60 * 60 * 24)
                                      : 0;
                                  if (diffDays > 60) {
                                    setError("En fazla 60 günlük bir aralık seçebilirsiniz.");
                                  } else {
                                    setEndDate(newEnd);
                                  }
                                }}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                inputProps={{
                                  max: new Date().toISOString().split("T")[0],
                                  min: (() => {
                                    const d = new Date();
                                    d.setDate(d.getDate() - 60);
                                    return d.toISOString().split("T")[0];
                                  })()
                                }}
                              />
                            </FormControl>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  {/* Right panel: summary tables */}
                  <Grid item xs={12} md={9}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" mt={2} mb={1}>
                          Ziyaretçi İzin Grafikleri
                        </Typography>
                        <Box height={650} mb={3}>
                          <div ref={chartRef} />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Removed: Add Cookie Dialog */}

      <Footer />

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default CookieCategories;
